import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const CompanyContext = createContext({
  companyCurrency: 'INR',
  usdToInrRate: 84,
  convertToBase: (amount) => Number(amount) || 0,
  fmtCompact: (amount) => String(amount),
  fmtFull: (amount) => String(amount),
});

const CUR_SYM = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'AED ', SGD: 'S$', JPY: '¥' };

function compactFormat(n, currency) {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n) || 0;
  const sym = CUR_SYM[currency] || `${currency} `;
  if (currency === 'INR') {
    if (v >= 10_000_000) return `${sym}${(v / 10_000_000).toFixed(1)}Cr`;
    if (v >= 100_000)    return `${sym}${(v / 100_000).toFixed(1)}L`;
    if (v >= 1_000)      return `${sym}${(v / 1_000).toFixed(0)}K`;
    return `${sym}${v.toFixed(0)}`;
  }
  if (v >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${sym}${(v / 1_000).toFixed(1)}K`;
  return `${sym}${v.toFixed(0)}`;
}

export function CompanyProvider({ children }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});

  const refreshSettings = () => {
    if (!user) return;
    api.get('/company-settings')
      .then(r => setSettings(r.data.data || {}))
      .catch(() => {});
  };

  useEffect(() => {
    if (!user) { setSettings({}); return; }
    api.get('/company-settings')
      .then(r => setSettings(r.data.data || {}))
      .catch(() => {});
  }, [user]);

  const companyCurrency = settings.default_currency || 'INR';
  const usdToInrRate    = Math.max(1, Number(settings.usd_to_inr_rate) || 84);

  // Convert an amount from `fromCurrency` into companyCurrency for aggregation
  // Supports INR ↔ USD; other currencies pass through as-is
  const convertToBase = (amount, fromCurrency) => {
    const n = Number(amount) || 0;
    if (!fromCurrency || fromCurrency === companyCurrency) return n;
    if (fromCurrency === 'USD' && companyCurrency === 'INR') return n * usdToInrRate;
    if (fromCurrency === 'INR' && companyCurrency === 'USD') return n / usdToInrRate;
    return n;
  };

  // Convert companyCurrency amount to a display currency (for "view in USD" toggle)
  const convertForDisplay = (amount, toCurrency) => {
    const n = Number(amount) || 0;
    if (!toCurrency || toCurrency === companyCurrency) return n;
    if (companyCurrency === 'INR' && toCurrency === 'USD') return n / usdToInrRate;
    if (companyCurrency === 'USD' && toCurrency === 'INR') return n * usdToInrRate;
    return n;
  };

  const fmtCompact = (amount, fromCurrency) =>
    compactFormat(convertToBase(amount, fromCurrency), companyCurrency);

  const fmtFull = (amount, fromCurrency) => {
    const n = convertToBase(amount, fromCurrency);
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: companyCurrency, maximumFractionDigits: 0,
      }).format(n);
    } catch {
      return compactFormat(n, companyCurrency);
    }
  };

  return (
    <CompanyContext.Provider value={{
      companyCurrency, usdToInrRate,
      convertToBase, convertForDisplay,
      fmtCompact, fmtFull,
      settings,
      refreshSettings,
    }}>
      {children}
    </CompanyContext.Provider>
  );
}

export const useCompany = () => useContext(CompanyContext);
